import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Row, Col, Card, CardBody, Form, FormGroup, Label, Input,
    Button, Table, InputGroup, InputGroupText
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FiTruck, FiPackage, FiFileText, FiTrash2, FiPlusCircle, FiHash } from "react-icons/fi";

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const EditInvoice = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [clients, setClients] = useState([]);
    const [subClients, setSubClients] = useState([]);
    const [lorries, setLorries] = useState([]);
    const [products, setProducts] = useState([]);

    const [formData, setFormData] = useState({
        invoice_id: id,
        invoice_no: "",
        invoice_date: dayjs().format('YYYY-MM-DD'),
        client_id: null,
        subclient_id: "",
        lorry_id: null,
        hsn_code: "",
        rate_per_kg: "",
        actual_rate_per_kg: 0, // 🔥 Default-ah 0 set panniten machan
        cgst: 2.5,
        sgst: 2.5,
        items: []
    });

    useEffect(() => {
        const fetchAllData = async () => {
            dispatch(loadingTrue());
            try {
                const [cRes, lRes, pRes, vRes] = await Promise.all([
                    Handler({ method: "GET", url: "/clients/list.php" }),
                    Handler({ method: "GET", url: "/lorry/list.php" }),
                    Handler({ method: "GET", url: "/product/list.php" }),
                    Handler({ method: "GET", url: `/invoice/view.php?id=${id}` })
                ]);

                const clientOpts = cRes?.data?.map(c => ({
                    value: Number(c.id),
                    label: c.client?.name?.toUpperCase() || "UNKNOWN"
                })) || [];

                const lorryOpts = lRes?.data?.map(l => ({ value: Number(l.id), label: l.lorry_number })) || [];
                const productOpts = pRes?.data?.map(p => ({ value: Number(p.id), label: p.name })) || [];

                setClients(clientOpts);
                setLorries(lorryOpts);
                setProducts(productOpts);

                if (vRes?.success) {
                    const resData = vRes.data;
                    const inv = resData.invoice;
                    const itemsData = resData.items;
                    const summaryData = resData.summary;

                    const latestLog = resData.logs && resData.logs.length > 0 ? resData.logs[0].data : null;
                    const logBags = latestLog ? Number(latestLog.total_bags) : 0;

                    const selectedClientId = Number(resData.client?.id);

                    if (selectedClientId) {
                        const scRes = await Handler({ method: "GET", url: `/sub_client/list.php?client_id=${selectedClientId}&status=1` });
                        if (scRes?.success) {
                            setSubClients(scRes.data.map(sc => ({
                                value: sc.id,
                                label: sc.sub_client?.name?.toUpperCase() || "UNKNOWN"
                            })));
                        }
                    }

                    // 🔥 Precision error fix: math logic mathiten
                    const cost = summaryData.total_kg > 0
                        ? (summaryData.subtotal - summaryData.profit) / summaryData.total_kg
                        : 0;

                    setFormData({
                        invoice_id: id,
                        invoice_no: inv.invoice_no || "",
                        invoice_date: inv.invoice_date || dayjs().format('YYYY-MM-DD'),
                        client_id: clientOpts.find(c => c.value === selectedClientId) || null,
                        subclient_id: inv.sub_client_id || "",
                        lorry_id: lorryOpts.find(l => l.value === Number(resData.lorry?.id)) || null,
                        hsn_code: inv.hsn_code || "5201",
                        rate_per_kg: itemsData[0]?.rate || 0,
                        actual_rate_per_kg: Math.abs(cost) < 0.01 ? 0 : cost.toFixed(2), // 🔥 0-ku close-ah irundha 0-nu mathidum
                        cgst: 2.5,
                        sgst: 2.5,
                        items: itemsData.map((item, index) => ({
                            product_id: Number(item.product_id),
                            bags: item.quantity?.bags || (itemsData.length === 1 ? logBags : 0),
                            total_kg: item.quantity?.kg || item.total_kg || 0
                        }))
                    });
                }
            } catch (err) {
                console.error("Fetch error:", err);
                toast.error("Failed to load invoice details");
            } finally { dispatch(loadingFalse()); }
        };
        fetchAllData();
    }, [id, dispatch]);

    useEffect(() => {
        const loadNewSubClients = async () => {
            if (formData.client_id?.value) {
                try {
                    const res = await Handler({ method: "GET", url: `/sub_client/list.php?client_id=${formData.client_id.value}&status=1` });
                    if (res?.success) {
                        setSubClients(res.data.map(sc => ({
                            value: sc.id,
                            label: sc.sub_client?.name?.toUpperCase() || "UNKNOWN"
                        })));
                    }
                } catch (err) { console.error("Sub-client fetch error"); }
            }
        };
        loadNewSubClients();
    }, [formData.client_id]);

    const addItem = () => {
        setFormData({ ...formData, items: [...formData.items, { product_id: "", bags: "", total_kg: "" }] });
    };

    const removeItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };

    const handleItemChange = (index, field, value) => {
        const val = value < 0 ? 0 : value;
        const newItems = [...formData.items];
        newItems[index][field] = val;
        setFormData({ ...formData, items: newItems });
    };

    const calculateSummary = () => {
        let totalKg = 0;
        formData.items?.forEach(item => {
            totalKg += parseFloat(item.total_kg || 0);
        });
        const subtotal = totalKg * parseFloat(formData.rate_per_kg || 0);
        const actualTotal = totalKg * parseFloat(formData.actual_rate_per_kg || 0);
        const profit = subtotal - actualTotal;

        // 🔥 Grand Total-la mattum Math.round add pannittaen machan (Create page-la irukara maariyae)
        const totalAmount = Math.round(subtotal + (subtotal * (parseFloat(formData.cgst) + parseFloat(formData.sgst)) / 100));

        return { totalKg, subtotal, totalAmount, profit };
    };

    const summary = calculateSummary();

    const handleSubmit = async (e) => {
        e.preventDefault();
        dispatch(loadingTrue());
        const payload = {
            ...formData,
            client_id: formData.client_id?.value,
            subclient_id: formData.subclient_id || null,
            lorry_id: formData.lorry_id?.value,
            rate_per_kg: parseFloat(formData.rate_per_kg || 0).toFixed(2),
            actual_rate_per_kg: formData.actual_rate_per_kg !== "" ? parseFloat(formData.actual_rate_per_kg).toFixed(2) : 0,
            items: formData.items.map(it => ({
                product_id: it.product_id,
                bags: Math.round(it.bags || 0),
                total_kg: parseFloat(it.total_kg || 0).toFixed(2)
            }))
        };

        try {
            const res = await Handler({ method: "POST", url: "/invoice/update.php", data: payload });
            if (res?.success) {
                Swal.fire("Updated!", "Invoice updated successfully", "success");
                navigate("/invoices");
            } else {
                toast.error(res?.message || "Update failed");
            }
        } catch (err) { toast.error("Server error occurred"); }
        finally { dispatch(loadingFalse()); }
    };

    const blockInvalidChar = (e) => ["-", "e", "E"].includes(e.key) && e.preventDefault();

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '45px',
            borderRadius: '10px',
            borderColor: state.isFocused ? '#2e4a3d' : '#dee2e6',
            backgroundColor: '#f8f9fa'
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 p-md-4 bg-light min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold text-dark m-0">Edit Dispatch Invoice</h3>
                        <p className="text-muted small m-0">Invoice No: <strong>{formData.invoice_no}</strong></p>
                    </div>
                    <Button color="secondary" outline size="sm" onClick={() => navigate(-1)} className="rounded-pill px-3">
                        <i className="bx bx-arrow-back me-1"></i> Back
                    </Button>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Row className="g-4">
                        <Col lg={8}>
                            {/* Invoice Details Card */}
                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-4">
                                    <h6 className="fw-bold mb-4 text-uppercase ls-1"><FiFileText className="me-2" /> Invoice Details</h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold text-uppercase">Invoice No *</Label>
                                                <InputGroup className="bg-light rounded-3 overflow-hidden border border-secondary border-opacity-25">
                                                    <InputGroupText className="bg-transparent border-0"><FiHash /></InputGroupText>
                                                    <Input
                                                        type="text"
                                                        className="bg-transparent border-0 fw-bold"
                                                        style={{ height: '48px' }}
                                                        value={formData.invoice_no}
                                                        onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="d-flex flex-column">
                                                <Label className="small fw-bold text-uppercase">Invoice Date *</Label>
                                                <DatePicker
                                                    disableFuture
                                                    format="DD/MM/YYYY"
                                                    value={dayjs(formData.invoice_date)}
                                                    onChange={(newValue) => {
                                                        setFormData({ ...formData, invoice_date: newValue ? newValue.format('YYYY-MM-DD') : '' });
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            variant: "outlined",
                                                            sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: '#f8f9fa',
                                                                    borderRadius: '10px',
                                                                    height: '48px',
                                                                    '& fieldset': { border: '1px solid rgba(0,0,0,0.1)' }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            {/* Logistics Card */}
                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-4">
                                    <h6 className="fw-bold mb-4 text-uppercase ls-1"><FiTruck className="me-2" /> Logistics & Client</h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Label className="small fw-bold">Client / Party</Label>
                                            <Select styles={customSelectStyles} options={clients} value={formData.client_id} onChange={(opt) => setFormData({ ...formData, client_id: opt, subclient_id: "" })} placeholder="Select Client" />
                                        </Col>
                                        <Col md={6}>
                                            <Label className="small fw-bold">Sub-Client (Branch)</Label>
                                            <Select
                                                styles={customSelectStyles}
                                                options={subClients}
                                                isClearable
                                                isDisabled={!formData.client_id}
                                                value={subClients.find(s => s.value === formData.subclient_id) || null}
                                                onChange={(opt) => setFormData({ ...formData, subclient_id: opt ? opt.value : "" })}
                                                placeholder="Select Branch..."
                                            />
                                        </Col>
                                        <Col md={12}>
                                            <Label className="small fw-bold">Lorry / Vehicle</Label>
                                            <Select styles={customSelectStyles} options={lorries} value={formData.lorry_id} onChange={(opt) => setFormData({ ...formData, lorry_id: opt })} placeholder="Select Lorry" />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            {/* Items Card */}
                            <Card className="border-0 shadow-sm rounded-4">
                                <CardBody className="p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h6 className="fw-bold m-0 text-uppercase ls-1"><FiPackage className="me-2" /> Dispatch Items</h6>
                                        <Button color="primary" size="sm" outline onClick={addItem} className="rounded-pill px-3 border-2">
                                            <FiPlusCircle className="me-1" /> Add Row
                                        </Button>
                                    </div>
                                    <div className="table-responsive">
                                        <Table className="align-middle border-0">
                                            <thead className="bg-light small text-uppercase">
                                                <tr className="text-muted">
                                                    <th className="border-0" style={{ width: '45%' }}>Product</th>
                                                    <th className="border-0 text-center" style={{ width: '20%' }}>Bags</th>
                                                    <th className="border-0 text-center" style={{ width: '25%' }}>Total KG</th>
                                                    <th className="border-0" style={{ width: '10%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.map((item, idx) => (
                                                    <tr key={idx} className="border-bottom-0">
                                                        <td className="ps-0 border-0">
                                                            <Select
                                                                options={products}
                                                                styles={customSelectStyles}
                                                                value={products.find(p => p.value === Number(item.product_id))}
                                                                onChange={(opt) => handleItemChange(idx, "product_id", opt.value)}
                                                            />
                                                        </td>
                                                        <td className="border-0 text-center">
                                                            <Input type="number" min="0" onKeyDown={blockInvalidChar} value={item.bags} onChange={(e) => handleItemChange(idx, "bags", e.target.value)} className="bg-light border-0 text-center" style={{ height: '45px', borderRadius: '10px' }} />
                                                        </td>
                                                        <td className="border-0 text-center pe-0">
                                                            <InputGroup className="bg-light rounded-3 overflow-hidden border-0">
                                                                <Input type="number" step="0.01" min="0" onKeyDown={blockInvalidChar} value={item.total_kg} onChange={(e) => handleItemChange(idx, "total_kg", e.target.value)} className="bg-transparent border-0 text-center" style={{ height: '45px' }} />
                                                                <InputGroupText className="bg-transparent border-0 small">kg</InputGroupText>
                                                            </InputGroup>
                                                        </td>
                                                        <td className="border-0 text-end pe-0">
                                                            {formData.items.length > 1 && (
                                                                <Button color="link" className="text-danger p-0" onClick={() => removeItem(idx)}><FiTrash2 size={18} /></Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Summary Column */}
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-4 sticky-top" style={{ top: '100px' }}>
                                <CardBody className="p-4">
                                    <h6 className="fw-bold mb-4 text-uppercase ls-1"><FiFileText className="me-2" /> Invoice Summary</h6>

                                    <FormGroup className="mb-3">
                                        <Label className="small fw-bold text-success text-uppercase">Selling Rate (per KG)</Label>
                                        <InputGroup className="bg-light rounded-3 overflow-hidden border">
                                            <InputGroupText className="bg-transparent border-0 text-success fw-bold">₹</InputGroupText>
                                            <Input className="bg-transparent border-0 fw-bold text-success" type="number" step="0.01" min="0" onKeyDown={blockInvalidChar} value={formData.rate_per_kg} onChange={(e) => setFormData({ ...formData, rate_per_kg: e.target.value })} />
                                        </InputGroup>
                                    </FormGroup>

                                    <FormGroup className="mb-4">
                                        <Label className="small fw-bold text-muted text-uppercase">Actual Cost (Optional)</Label>
                                        <InputGroup className="bg-light rounded-3 overflow-hidden border">
                                            <InputGroupText className="bg-transparent border-0 text-muted fw-bold">₹</InputGroupText>
                                            <Input className="bg-transparent border-0 fw-bold" type="number" step="0.01" min="0" onKeyDown={blockInvalidChar} value={formData.actual_rate_per_kg} onChange={(e) => setFormData({ ...formData, actual_rate_per_kg: e.target.value })} />
                                        </InputGroup>
                                    </FormGroup>

                                    <div className="bg-light p-3 rounded-4 mb-4 border">
                                        <div className="d-flex justify-content-between mb-2 small text-muted">
                                            <span>Total Weight:</span>
                                            <span className="fw-bold text-dark">{parseFloat(summary.totalKg).toFixed(2)} KG</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2 small text-muted">
                                            <span>Profit:</span>
                                            <span className={`fw-bold ${summary.profit >= 0 ? 'text-success' : 'text-danger'}`}>₹ {summary.profit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="d-flex justify-content-between pt-2 border-top">
                                            <span className="fw-bold">Taxable Amt:</span>
                                            <span className="fw-bold">₹ {summary.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        </div>
                                    </div>

                                    <div className="bg-primary text-white p-4 rounded-4 text-center mb-4">
                                        <p className="small opacity-75 mb-1 text-uppercase fw-bold">Grand Total</p>
                                        <h2 className="fw-bold m-0">₹ {summary.totalAmount?.toLocaleString()}</h2>
                                    </div>

                                    <Button color="primary" block className="py-3 fw-bold fs-6 shadow-sm rounded-3 border-0" type="submit">
                                        SAVE UPDATES
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>
        </LocalizationProvider>
    );
};

export default EditInvoice;